import { Box, Stack, Typography } from '@mui/material';
import EnergySavingsLeafOutlined from '@mui/icons-material/EnergySavingsLeafOutlined';
import VerifiedOutlined from '@mui/icons-material/VerifiedOutlined';
import ghgLogo from '../../../../assets/images/ghg.png';
import { AUTH_TOKENS, AuthBranding, BRAND_DISPLAY_FONT } from './auth-branding';

// Single responsive breakpoint from the handoff (row <-> column at 940px).
const DESKTOP = '@media (min-width:940px)';

interface AuthBrandPanelProps {
  branding: AuthBranding;
  /** Editorial block (eyebrow/headline/sub). Gaia + partners-with-copy show it. */
  showEditorial?: boolean;
  /** Reporting-standard credibility block. Toggleable in case a partner opts out. */
  showTrustMarks?: boolean;
}

export const AuthBrandPanel = ({
  branding,
  showEditorial = true,
  showTrustMarks = true,
}: AuthBrandPanelProps) => {
  // Copy comes from branding (Gaia defaults, or partner-supplied); each element
  // renders only when non-empty, so a partner who sets only a headline shows
  // just the headline.
  const { eyebrow, headline, subheadline } = branding;
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
        minHeight: 188,
        p: '24px',
        [DESKTOP]: {
          flex: '1 1 0',
          minHeight: '100vh',
          p: '48px 52px',
        },
      }}
    >
      <Box
        component="img"
        src={branding.coverSrc}
        alt=""
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
      />
      <Box sx={{ position: 'absolute', inset: 0, background: branding.wash }} />

      {/* Logo — visible at every width. Gaia's reversed wordmark sits directly on
          the wash; a partner logo gets a light plate so it stays legible. */}
      {branding.logoNeedsPlate ? (
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            alignSelf: 'flex-start',
            display: 'inline-flex',
            alignItems: 'center',
            background: 'rgba(255,255,255,0.95)',
            borderRadius: '12px',
            p: '10px 14px',
            boxShadow: '0 8px 24px rgba(17,30,33,0.22)',
          }}
        >
          <Box
            component="img"
            src={branding.logoSrc}
            alt={branding.productName}
            sx={{ height: 32, width: 'auto', maxWidth: 150, display: 'block' }}
          />
        </Box>
      ) : (
        <Box
          component="img"
          src={branding.logoSrc}
          alt={branding.productName}
          sx={{ position: 'relative', zIndex: 1, width: 104, height: 'auto', alignSelf: 'flex-start' }}
        />
      )}

      {/* Editorial block — desktop only */}
      {showEditorial && (
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            flexDirection: 'column',
            maxWidth: 460,
            display: 'none',
            [DESKTOP]: { display: 'flex' },
          }}
        >
          {eyebrow && (
            <Box
              sx={{
                display: 'inline-flex',
                alignSelf: 'flex-start',
                alignItems: 'center',
                gap: '8px',
                mb: '22px',
                p: '6px 13px 6px 10px',
                borderRadius: '999px',
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.22)',
                backdropFilter: 'blur(3px)',
              }}
            >
              <EnergySavingsLeafOutlined sx={{ fontSize: 16, color: 'primary.light' }} />
              <Typography
                component="span"
                sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'rgba(255,255,255,0.92)' }}
              >
                {eyebrow}
              </Typography>
            </Box>
          )}
          {headline && (
            <Typography
              component="h1"
              sx={{
                m: 0,
                mb: '20px',
                fontSize: '3.3rem',
                lineHeight: 1.04,
                fontFamily: BRAND_DISPLAY_FONT,
                letterSpacing: '-0.02em',
                color: '#fff',
                whiteSpace: 'pre-line',
              }}
            >
              {headline}
            </Typography>
          )}
          {subheadline && (
            <Typography
              sx={{
                m: 0,
                fontSize: '1.125rem',
                lineHeight: 1.55,
                color: 'rgba(255,255,255,0.86)',
                maxWidth: 420,
              }}
            >
              {subheadline}
            </Typography>
          )}
        </Box>
      )}

      {/* Trust marks — desktop only */}
      {showTrustMarks && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'none',
            [DESKTOP]: { display: 'flex' },
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              background: 'rgba(255,255,255,0.95)',
              borderRadius: '12px',
              p: '11px 16px',
              boxShadow: '0 8px 24px rgba(17,30,33,0.22)',
            }}
          >
            <Box component="img" src={ghgLogo} alt="GHG Protocol" sx={{ height: 30, width: 'auto', display: 'block' }} />
            <Box component="span" sx={{ width: '1px', height: 26, backgroundColor: AUTH_TOKENS.border }} />
            <Stack direction="row" alignItems="center" spacing="7px" sx={{ color: branding.secrInk }}>
              <VerifiedOutlined sx={{ fontSize: 22 }} />
              <Typography component="span" sx={{ fontSize: '0.9rem', fontFamily: BRAND_DISPLAY_FONT }}>
                SECR ready
              </Typography>
            </Stack>
          </Box>
          <Typography
            component="span"
            sx={{ fontSize: '0.8125rem', lineHeight: 1.4, color: 'rgba(255,255,255,0.72)', maxWidth: 150 }}
          >
            Aligned to the standards your report is judged by
          </Typography>
        </Stack>
      )}
    </Box>
  );
};
